import ArrowRightIcons from '@/icons/ArrowRightIcons'
import TrendingDownIcons from '@/icons/TrendingDownIcons'
import TrendingUpIcons from '@/icons/TrendingUpIcons'
import React, { useMemo } from 'react'
import { Link } from 'react-router-dom'
import { Typogaphy } from 'vnetwork-ui'
import CardMinimize from './Minimize'

export const ICON_COLORS_MAP = {
    primary: { bg: '#D0D5FF', color: '#1A237E' },
    warning: { bg: '#FFFB00', color: '#FFD000' },
    lead: { bg: '#8280FF', color: '#6CA9FF' },
    processing: { bg: '#FF8400', color: '#E26F23' },
    error: { bg: '#EB0004', color: '#EB0004' },
    success: { bg: '#008556', color: '#008556' },
}

export type CardColorTypes = keyof typeof ICON_COLORS_MAP
export interface StatisticalCardProps {
    label: string
    value?: number
    description?: string
    scale?: number
    type?: 'default' | 'highlighted'
    icon?: React.ReactNode
    color?: CardColorTypes
    className?: string
    href?: string
    minimize?: boolean
}

const StatisticalCard: React.FC<StatisticalCardProps> = ({
    label,
    value,
    description = 'Up from yesterday',
    scale,
    type = 'default',
    icon,
    color = 'primary',
    href,
    minimize = false,
    className,
}) => {
    const iconColor = useMemo(() => ICON_COLORS_MAP[color], [color])

    if (minimize)
        return (
            <CardMinimize
                icon={icon}
                href={href}
                className={className}
                value={value}
                label={label}
                iconColor={iconColor}
            />
        )
    return (
        <div
            className={`group hover:cursor-pointer flex justify-between bg-white p-4 gap-2 rounded-lg ${type === 'highlighted' ? 'col-span-6' : 'col-span-3 flex-col'} ${className}`}
        >
            <div className="flex flex-col justify-between gap-2">
                <Typogaphy className="font-semibold text-primary text-md py-[10px]">
                    {label}
                </Typogaphy>
                <div className="flex items-center gap-2">
                    <div
                        className="w-8 h-8 flex items-center justify-center rounded-full border"
                        style={{ backgroundColor: `${iconColor?.color}36` }}
                    >
                        <div
                            style={{ color: iconColor.color }}
                            className="w-4 h-4"
                        >
                            {icon}
                        </div>
                    </div>
                    <Typogaphy className="font-bold text-2xl">
                        {value}
                    </Typogaphy>
                </div>
            </div>

            <div
                className={`flex justify-between gap-2 ${type === 'default' ? 'items-center' : 'flex-col'}`}
            >
                <div className="flex flex-col justify-between h-full">
                    <div
                        className={`flex gap-2 items-center py-[10px] ${type === 'highlighted' ? 'justify-end' : ''}`}
                    >
                        {scale && scale < 0 ? (
                            <TrendingDownIcons className="w-4 h-4 text-error" />
                        ) : (
                            <TrendingUpIcons className="w-4 h-4 text-[#00B69B]" />
                        )}
                        <Typogaphy
                            className={`font-medium text-xs ${scale && scale < 0 ? 'text-error' : 'text-[#00B69B]'}`}
                        >
                            {(scale && Math.abs(scale)) || '--'} %
                        </Typogaphy>
                    </div>
                    <div
                        className={`flex ${type === 'default' ? 'justify-between' : ''}`}
                    >
                        <Typogaphy className="font-medium text-gray-500 text-xs capitalize">
                            {description}
                        </Typogaphy>
                    </div>
                </div>

                {type === 'default' && !!href && (
                    <div className="w-9 h-9 bg-[#F9FBFF] border flex items-center justify-center rounded-[4px] border-[#ECF4FF]">
                        <Link to={href} replace>
                            <ArrowRightIcons className="text-[#C1DBFF] w-6 h-[18px] group-hover:text-primary transition-colors duration-200" />
                        </Link>
                    </div>
                )}
            </div>
        </div>
    )
}

export default StatisticalCard

export const StatisticalCardSkeleton = ({
    minimize = false,
    highlighted = false,
}: {
    minimize?: boolean
    highlighted?: boolean
}) => {
    if (minimize) {
        return (
            <div className="flex items-center justify-between bg-white px-4 py-2 rounded-lg animate-pulse">
                <div className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded-full bg-gray-300" />
                    <div className="h-4 w-24 bg-gray-200 rounded" />
                </div>
                <div className="flex items-center gap-2">
                    <div className="h-4 w-8 bg-gray-200 rounded" />
                    <div className="h-4 w-4 bg-gray-300 rounded-full" />
                </div>
            </div>
        )
    }

    return (
        <div
            className={`flex justify-between bg-white p-4 gap-2 rounded-lg animate-pulse ${highlighted ? 'col-span-6' : 'col-span-3 flex-col'}`}
        >
            <div className="flex flex-col gap-2">
                <div className="h-4 w-32 bg-gray-200 rounded" />
                <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-gray-300" />
                    <div className="h-6 w-24 bg-gray-300 rounded" />
                </div>
            </div>
            <div className="flex flex-col gap-2">
                <div className="flex items-center gap-2">
                    <div className="w-4 h-4 bg-gray-300 rounded" />
                    <div className="h-3 w-10 bg-gray-200 rounded" />
                </div>
                <div className="h-3 w-24 bg-gray-200 rounded" />
            </div>
        </div>
    )
}
